import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { FaFilter, FaRobot } from "react-icons/fa";
import usePagination from "src/customHooks/usePagination";
import PageHeader from "src/components/pageHeader";
import Pagination from "src/components/pagination";
import PageLoading from "src/components/pageLoading";
import AIScenariosSearch from "../components/ai-scenarios-search";
import AIScenariosTable from "../components/ai-scenarios-table";
import { AiScenario } from "../entities/ai-scenario.entity";
import {
  CustomAIScenariosFilter,
  useCountCustomAIScenariosQuery,
  useGetCustomAIScenariosQuery,
} from "../ai-scenario.service";

const StatCard = ({ title, count, icon, colorClass, borderClass, bgClass }: any) => (
  <div className={`p-4 rounded-xl border ${borderClass} ${bgClass} shadow-sm flex items-center justify-between`}>
    <div>
      <p className="text-gray-500 text-xs font-semibold uppercase tracking-wider mb-1">{title}</p>
      <h3 className="text-2xl font-bold text-gray-800">{count ?? "-"}</h3>
    </div>
    <div className={`p-3 rounded-full ${colorClass} bg-white bg-opacity-70`}>
      {icon}
    </div>
  </div>
);

const AICustomTopicsPageContent = ({
  initialQuery,
  onApply,
}: {
  initialQuery: CustomAIScenariosFilter;
  onApply: (filters: CustomAIScenariosFilter) => void;
}) => {
  const hasActiveFilters = Object.keys(initialQuery).length > 0;
  const { data: totalScenarios } = useCountCustomAIScenariosQuery({ where: {} });
  const { data: matchingScenarios } = useCountCustomAIScenariosQuery({
    where: initialQuery,
  });

  const {
    data,
    numberOfPages,
    currentPage,
    next,
    prev,
    isSuccess,
    isError,
    isLoading,
    isFetching,
  } = usePagination<AiScenario & { id: string }>({
    useGetDataQuery: useGetCustomAIScenariosQuery,
    useCountData: useCountCustomAIScenariosQuery,
    initQuery: initialQuery,
  });

  return (
    <div className="page_container space-y-6">
      <PageHeader text="AI Custom Topics" />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <StatCard
          title="Total Scenarios"
          count={totalScenarios}
          icon={<FaRobot className="text-xl text-indigo-600" />}
          colorClass="text-indigo-600"
          borderClass="border-indigo-100"
          bgClass="bg-gradient-to-br from-indigo-50 to-slate-50"
        />
        <StatCard
          title={hasActiveFilters ? "Matching Filters" : "Current Results"}
          count={hasActiveFilters ? matchingScenarios : totalScenarios}
          icon={<FaFilter className="text-xl text-cyan-600" />}
          colorClass="text-cyan-600"
          borderClass="border-cyan-100"
          bgClass="bg-gradient-to-br from-cyan-50 to-sky-50"
        />
      </div>

      <AIScenariosSearch initialQuery={initialQuery} onApply={onApply} />

      {isSuccess ? (
        <>
          <AIScenariosTable data={data} />
          <Pagination
            numberOfPages={numberOfPages}
            currentPage={currentPage}
            prev={prev}
            next={next}
            isLoading={isLoading || isFetching}
          />
        </>
      ) : isError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold text-red-800 mb-2">
            Error Loading AI Custom Topics
          </h3>
          <p className="text-red-600">
            There was an error fetching the AI custom topics data. Please check the console for more details.
          </p>
        </div>
      ) : (
        <PageLoading />
      )}
    </div>
  );
};

const AICustomTopicsPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const initialQuery = useMemo((): CustomAIScenariosFilter => {
    const scenarioId = searchParams.get("scenarioId");
    const userId = searchParams.get("userId");

    return {
      ...(scenarioId ? { scenarioId } : {}),
      ...(userId ? { userId } : {}),
    };
  }, [searchParams]);

  const applyFilters = useCallback(
    (filters: CustomAIScenariosFilter) => {
      const nextParams = new URLSearchParams();

      Object.entries(filters).forEach(([key, value]) => {
        if (typeof value === "string" && value.trim()) {
          nextParams.set(key, value.trim());
        }
      });

      setSearchParams(nextParams, { replace: true });
    },
    [setSearchParams]
  );

  return (
    <AICustomTopicsPageContent
      key={searchParams.toString()}
      initialQuery={initialQuery}
      onApply={applyFilters}
    />
  );
};

export default AICustomTopicsPage;
